import { css } from '@emotion/react';
import { useMemo, useCallback, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  MentorQuestionAnswer,
} from 'redux/actions/mentoring-program-participant-driven';
import NvFiltersBar from 'shared/components/filters/nv-filters-bar';
import { NvDropdownCheckbox, NvDropdownHeader, NvDropdownRadio } from 'shared/components/inputs/nv-dropdown';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import { white } from 'styles/global_defaults/colors';
import { useSelector } from 'react-redux';
import { selectMentorsFilters } from 'redux/selectors/mentorship-program-participant-driven';
import { useFormContext } from 'react-hook-form';
import { config } from '@config/pendo.config.json';
import { doubleModalZIndex } from 'shared/components/nv-modal';

import t from 'react-translate';
import { TIMEZONE_QUESTION_TEXT } from 'athena/components/mentoring-program/program-configuration/program-profile-setup/org-level';

const BROWSE_MENTORS_FILTERS_MIN_HEIGHT = 55;
const MAX_FILTER_WIDTH = 300;

type BrowseMentorsFiltersProps = {
  isContentScrolled?: boolean;
};

const browseMentorsFiltersStyles = css`
  flex-shrink: 0;
  min-height: ${BROWSE_MENTORS_FILTERS_MIN_HEIGHT}px;

  &.scrolled {
    box-shadow: 1px 2px 2px 0px rgba(0, 0, 0, 0.1);
  }

  .browse-mentors-filters-bar {
    align-items: flex-start;
    background: ${white};
    border-bottom: none;

    .filters {
      flex: 1 1 0%;
    }

    .filters div.bs4-dropdown .bs4-dropdown-menu.filter-menu.show {
      max-width: ${MAX_FILTER_WIDTH}px;
    }
    .timezone-filter .filter-menu {
      max-width: unset !important;
    }
  }

  .bs4-dropdown {
    max-width: ${MAX_FILTER_WIDTH}px;
  }
`;

const TIMEZONE_TRANSLATION_MAP = {
  ANY_TIME_ZONE: () => (
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL.FILTERS.TIME_ZONE.ANY_TIME_ZONE()
  ),
  SAME_TIME_ZONE: () => (
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL.FILTERS.TIME_ZONE.SAME_TIME_ZONE()
  ),
};

const getLabelText = (key: string) => {
  if (TIMEZONE_TRANSLATION_MAP[key]) {
    return TIMEZONE_TRANSLATION_MAP[key]();
  }
  const hours = key.replace('PLUS_', '').replace('_HOURS', '');
  return t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL.FILTERS.TIME_ZONE.PLUS_X_HOUR(hours);
};

const getTimeZoneItems = (
  id: number,
  pendingFilters: Record<number, string[]>,
  onChange: (id: number, filter: string, isChecked: boolean) => void,
) => {
  const timezoneOptions = [
    'ANY_TIME_ZONE',
    'SAME_TIME_ZONE',
    ...Array.from({ length: 8 }, (_, i) => `PLUS_${i + 1}_HOURS`),
  ];

  const isItemChecked = (answer: string, index: number) => (
    pendingFilters[id]?.includes((index - 1).toString())
    || (!pendingFilters[id] && index === 0)
  );

  return timezoneOptions.map((answer, index) => ({
    id: `${id}-${index}-timezone-offset`,
    type: 'radio',
    label: (
      <NvTruncatedTextWithTooltip
        text={getLabelText(answer)}
        className='truncated-text text-regular black'
        zIndex={doubleModalZIndex}
      />
    ),
    group: 'time_zone_offset',
    class: 'filter-item p-0',
    isChecked: isItemChecked(answer, index),
    callback: () => {
      onChange(id, (index - 1).toString(), !pendingFilters[id]?.includes(answer));
    },
  }));
};

const getIsSelected = (selectedFilters: string[], isTimeZoneQuestion: boolean) => {
  if (!isTimeZoneQuestion) {
    return !!selectedFilters?.length;
  }

  return !!selectedFilters?.length && !selectedFilters?.includes('-1');
};

const getFilterTitle = (
  questionText: string,
  isTimeZoneQuestion: boolean,
  selectedFilter: string[],
) => {
  if (!isTimeZoneQuestion) {
    return !selectedFilter?.length
      ? questionText
      : `${questionText} (${selectedFilter?.length})`;
  }
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL.FILTERS.TIME_ZONE.TITLE;

  if (!selectedFilter?.length || selectedFilter?.includes('-1')) {
    return translationBase.ANY_TIME_ZONE();
  }

  if (selectedFilter?.includes('0')) {
    return translationBase.SAME_TIME_ZONE();
  }

  return translationBase.PLUS_X_HOUR(selectedFilter[0]);
};

const sortAlphabetically = (a: string, b: string) => a.localeCompare(b);

const getFilters = (
  filters: MentorQuestionAnswer[],
  onChange: (id: number, filter: string, isChecked: boolean) => void,
  selectedFilters: Record<number, string[]>,
  pendingFilters: Record<number, string[]>,
  onToggle: (questionId: number) => (isOpen: boolean) => void,
) => filters
  .filter((item) => item.searchableAnswers?.length > 0 || item.questionText === TIMEZONE_QUESTION_TEXT)
  .map((item) => {
    const { id, questionText, searchableAnswers } = item;
    const isTimeZoneQuestion = questionText === TIMEZONE_QUESTION_TEXT;
    let items = [];
    if (isTimeZoneQuestion) {
      items = getTimeZoneItems(id, pendingFilters, onChange);
    } else {
      items = [...searchableAnswers]
        .sort(sortAlphabetically)
        .map((answer, index) => ({
          id: `${id}-${index}-${answer}`,
          type: 'checkbox',
          label: (
            <NvTruncatedTextWithTooltip
              text={answer}
              className='truncated-text'
              zIndex={doubleModalZIndex}
            />
          ),
          name: answer,
          class: 'filter-item',
          checked: pendingFilters[id]?.includes(answer) || false,
          onChanged: (isChecked: boolean) => {
            onChange(id, answer, isChecked);
          },
        }));
    }

    const questionTitle = isTimeZoneQuestion
      ? t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.HEADERS.TIME_ZONE()
      : questionText;

    return {
      title: getFilterTitle(questionText, isTimeZoneQuestion, selectedFilters[id]),
      selected: getIsSelected(selectedFilters[id], isTimeZoneQuestion),
      onToggle: onToggle(id),
      items: [{
        type: 'header',
        title: questionTitle,
        class: 'title text-wrap mb-2',
      }, ...items] as Array<NvDropdownHeader | NvDropdownCheckbox | NvDropdownRadio>,
      ...(isTimeZoneQuestion && { className: 'timezone-filter' }),
    };
  });

const BrowseMentorsFilters = forwardRef(({
  isContentScrolled,
}: BrowseMentorsFiltersProps, ref) => {
  const filters = useSelector(selectMentorsFilters);
  const { watch, setValue } = useFormContext();
  const { filters: lastFilters } = watch();

  const [filtersSelection, setFiltersSelection] = useState<Record<number, string[]>>({});
  const [pendingSelection, setPendingSelection] = useState<Record<number, string[]>>({});

  const timeZoneQuestionId = filters.find((filter) => filter.questionText === TIMEZONE_QUESTION_TEXT)?.id;
  const hasFiltersSelectedOrApplied = Object.keys(filtersSelection).some((key) => {
    if (Number(key) === timeZoneQuestionId) {
      return !filtersSelection[key]?.includes('-1') || !!lastFilters[key];
    }
    return true;
  });

  useEffect(() => {
    setFiltersSelection(lastFilters || {});
    setPendingSelection(lastFilters || {});
    setValue('areFiltersSet', true, { shouldDirty: false, shouldValidate: false });
  }, [lastFilters, setValue]);

  const onChange = useCallback((question_id: number, filter: string, isChecked: boolean) => {
    setPendingSelection(prev => {
      let updated = { ...prev };

      const isTimezoneQuestion = filters.find(f => f.id === question_id)?.questionText === TIMEZONE_QUESTION_TEXT;

      if (isTimezoneQuestion && isChecked) {
        updated[question_id] = [filter];
      } else {
        const prevFilters = updated[question_id] || [];
        if (isChecked) {
          if (!prevFilters.includes(filter)) {
            updated[question_id] = [...prevFilters, filter];
          }
        } else {
          const filtered = prevFilters.filter((a) => a !== filter);
          if (filtered.length > 0) {
            updated[question_id] = filtered;
          } else {
            delete updated[question_id];
          }
        }
      }
      return updated;
    });
  }, [filters]);

  const handleDropdownToggle = useCallback((questionId: number) => (isOpen: boolean) => {
    if (!isOpen) {
      setFiltersSelection(prev => {
        const newState = { ...prev };
        if (pendingSelection[questionId] && pendingSelection[questionId].length > 0) {
          newState[questionId] = pendingSelection[questionId];
        } else {
          delete newState[questionId];
        }
        return newState;
      });
    }
  }, [pendingSelection]);

  const filtersList = useMemo(
    () => getFilters(filters, onChange, filtersSelection, pendingSelection, handleDropdownToggle),
    [filters, onChange, filtersSelection, pendingSelection, handleDropdownToggle],
  );

  const setFiltersAndFlag = useCallback((filtersObj: Record<number, string[]>) => {
    const filteredFilters = Object.fromEntries(
      Object.entries(filtersObj).filter(([key]) => (
        Number(key) !== timeZoneQuestionId || filtersObj[key][0] !== '-1'
      )),
    );
    setValue('filters', filteredFilters, { shouldDirty: true, shouldValidate: true });
    setValue('areFiltersSet', true, { shouldDirty: false, shouldValidate: false });
  }, [setValue]);

  const onApplyFilters = useCallback(() => {
    setFiltersAndFlag(filtersSelection);
  }, [filtersSelection, setFiltersAndFlag]);

  const onClearFilters = useCallback(() => {
    setFiltersSelection({});
    setPendingSelection({});
    setFiltersAndFlag({});
  }, [setFiltersAndFlag]);

  useImperativeHandle(ref, () => ({
    onClearFilters,
  }));

  if (filters.length === 0) {
    return null;
  }

  if (!filtersList.length) {
    return null;
  }

  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.findAMentor.modal;

  return (
    <div
      css={browseMentorsFiltersStyles}
      className={`align-content-center ${isContentScrolled ? 'scrolled' : ''}`}
    >
      <NvFiltersBar
        filters={filtersList}
        hasFiltersSelectedOrApplied={hasFiltersSelectedOrApplied}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        className='browse-mentors-filters-bar justify-content-between'
        resetDataQa={dataQATag.resetFilters}
        applyFiltersDataQa={dataQATag.applyFilters}
        expandFiltersListDataQa={dataQATag.expandFiltersList}
        collapseFiltersListDataQa={dataQATag.collapseFiltersList}
      />
    </div>
  );
});

export default BrowseMentorsFilters;
